import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "line1", "suburb", "city", "postcode", "region", "district", "latitude", "longitude", "placeId"]
  static values = { 
    fieldPrefix: String,
    specialForm: String // For handling special forms like information pack
  }

  connect() {
    this.autocompleteInstances = []
    this.boundHandleAddressAdded = this.handleAddressAdded.bind(this)
    this.initializeAutocomplete()
    
    // Listen for dynamic form address additions
    this.element.addEventListener('address:added', this.boundHandleAddressAdded)
  }

  disconnect() {
    this.element.removeEventListener('address:added', this.boundHandleAddressAdded)
    this.destroyAutocomplete()
  }

  initializeAutocomplete() {
    this.inputTargets.forEach(input => {
      if (!input.classList.contains('places-autocomplete-enabled')) {
        this.setupAutocomplete(input)
      }
    })
  }

  setupAutocomplete(input) {
    // Wait for Google Maps API to load
    if (typeof google === 'undefined' || !google.maps || !google.maps.places) {
      setTimeout(() => this.setupAutocomplete(input), 100)
      return
    }

    try {
      const autocomplete = new google.maps.places.Autocomplete(input, {
        componentRestrictions: { country: 'nz' },
        fields: ['address_components', 'geometry', 'formatted_address', 'place_id'],
        types: ['address']
      })

      autocomplete.addListener('place_changed', () => {
        this.handlePlaceChanged(input, autocomplete)
      })

      input.classList.add('places-autocomplete-enabled')
      input.setAttribute('placeholder', 'Start typing to choose your address from the list...')
      
      // Handle Enter key to prevent form submission when dropdown is open
      input.addEventListener('keydown', (event) => {
        this.handleKeyDown(event, input)
      })

      this.autocompleteInstances.push({ input, autocomplete })
    } catch (error) {
      console.warn('Failed to initialize Google Places Autocomplete:', error)
    }
  }

  handleKeyDown(event, input) {
    if (event.key === 'Enter') {
      const autocompleteContainer = document.querySelector('.pac-container')
      const hasPredictions = autocompleteContainer && 
                           autocompleteContainer.style.display !== 'none' &&
                           autocompleteContainer.querySelectorAll('.pac-item').length > 0
      
      if (hasPredictions) {
        event.preventDefault()
        event.stopPropagation()
        this.selectFirstPrediction()
      }
    }
  }

  selectFirstPrediction() {
    const autocompleteContainer = document.querySelector('.pac-container')
    if (!autocompleteContainer) return
    
    const firstItem = autocompleteContainer.querySelector('.pac-item')
    if (firstItem) {
      const mousedownEvent = new MouseEvent('mousedown', {
        bubbles: true,
        cancelable: true
      })
      firstItem.dispatchEvent(mousedownEvent)
    }
  }

  handlePlaceChanged(input, autocomplete) {
    const place = autocomplete.getPlace()
    
    if (!place || !place.address_components) {
      console.warn('No valid place selected')
      return
    }

    const addressData = this.parseGooglePlace(place)
    this.populateAddressFields(input, addressData)
    this.updateRegionDistrict(addressData)
    this.triggerFormUpdates()
  }

  parseGooglePlace(place) {
    const components = {}
    
    place.address_components.forEach(component => {
      const types = component.types
      
      if (types.includes('street_number')) {
        components.street_number = component.long_name
      }
      if (types.includes('route')) {
        components.route = component.long_name
      }
      if (types.includes('locality')) {
        components.locality = component.long_name
      }
      if (types.includes('administrative_area_level_2')) {
        components.district = component.long_name
      }
      if (types.includes('administrative_area_level_1')) {
        components.region = component.long_name
      }
      if (types.includes('postal_code')) {
        components.postal_code = component.long_name
      }
      if (types.includes('sublocality_level_1') || types.includes('neighborhood')) {
        components.suburb = component.long_name
      }
    })

    let addressLine1 = ''
    if (components.street_number && components.route) {
      addressLine1 = `${components.street_number} ${components.route}`
    } else if (components.route) {
      addressLine1 = components.route
    }

    return {
      line1: addressLine1,
      suburb: components.suburb || '',
      city: components.locality || '',
      postcode: components.postal_code || '',
      region: components.region || '',
      district: components.district || '',
      latitude: place.geometry?.location?.lat() || null,
      longitude: place.geometry?.location?.lng() || null,
      place_id: place.place_id || '',
      formatted_address: place.formatted_address || ''
    }
  }

  populateAddressFields(triggerInput, addressData) {
    // Handle special form types (like information pack)
    if (this.specialFormValue === 'information_pack' && triggerInput.id === 'information_pack_postal_detail_address_standard') {
      this.setFieldValue('information_pack_postal_detail[address]', addressData.line1)
      this.setFieldValue('information_pack_postal_detail[suburb]', addressData.suburb)
      this.setFieldValue('information_pack_postal_detail[city]', addressData.city)
      this.setFieldValue('information_pack_postal_detail[postcode]', addressData.postcode)
      return
    }

    // Standard handling using field prefix or target elements
    const fieldPrefix = this.fieldPrefixValue || this.getFieldPrefix(triggerInput)
    
    if (fieldPrefix) {
      // Use field prefix approach
      this.setFieldValue(`${fieldPrefix}[line1]`, addressData.line1)
      this.setFieldValue(`${fieldPrefix}[suburb]`, addressData.suburb)
      this.setFieldValue(`${fieldPrefix}[city]`, addressData.city)
      this.setFieldValue(`${fieldPrefix}[postcode]`, addressData.postcode)
      this.setFieldValue(`${fieldPrefix}[address]`, addressData.line1) // Alternative field name
      
      // Geographic data
      if (addressData.latitude && addressData.longitude) {
        this.setFieldValue(`${fieldPrefix}[latitude]`, addressData.latitude)
        this.setFieldValue(`${fieldPrefix}[longitude]`, addressData.longitude)
      }
      
      if (addressData.place_id) {
        this.setFieldValue(`${fieldPrefix}[place_id]`, addressData.place_id)
      }
    } else {
      // Use target elements approach
      this.setTargetValue('line1', addressData.line1)
      this.setTargetValue('suburb', addressData.suburb)
      this.setTargetValue('city', addressData.city)
      this.setTargetValue('postcode', addressData.postcode)
      this.setTargetValue('latitude', addressData.latitude)
      this.setTargetValue('longitude', addressData.longitude)
      this.setTargetValue('placeId', addressData.place_id)
    }
  }

  setFieldValue(fieldName, value) {
    if (!fieldName || value === undefined || value === null) return
    
    const field = this.element.querySelector(`[name="${fieldName}"]`)
    if (field && field.value !== value) {
      field.value = value
      field.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  setTargetValue(targetName, value) {
    if (value === undefined || value === null) return
    
    const targets = this[`${targetName}Targets`]
    if (targets && targets.length > 0) {
      targets.forEach(target => {
        if (target.value !== value) {
          target.value = value
          target.dispatchEvent(new Event('change', { bubbles: true }))
        }
      })
    }
  }

  getFieldPrefix(input) {
    const name = input.name
    const match = name.match(/^(.+)\[(line1|address)\]$/)
    return match ? match[1] : ''
  }

  updateRegionDistrict(addressData) {
    const fieldPrefix = this.fieldPrefixValue
    if (!fieldPrefix || !addressData.region) return

    const regionSelect = this.element.querySelector(`[name="${fieldPrefix}[region_id]"]`) || 
                        this.regionTargets[0]
    const districtSelect = this.element.querySelector(`[name="${fieldPrefix}[district_id]"]`) || 
                          this.districtTargets[0]

    if (!regionSelect) return

    // Find matching region option (case-insensitive)
    const regionOptions = Array.from(regionSelect.options)
    const matchingRegion = regionOptions.find(option => 
      option.text.toLowerCase().includes(addressData.region.toLowerCase()) ||
      addressData.region.toLowerCase().includes(option.text.toLowerCase())
    )

    if (matchingRegion) {
      regionSelect.value = matchingRegion.value
      regionSelect.dispatchEvent(new Event('change', { bubbles: true }))

      // Wait for district dropdown to update, then try to match district
      setTimeout(() => {
        if (districtSelect && addressData.district) {
          const districtOptions = Array.from(districtSelect.options)
          const matchingDistrict = districtOptions.find(option => 
            option.text.toLowerCase().includes(addressData.district.toLowerCase()) ||
            addressData.district.toLowerCase().includes(option.text.toLowerCase())
          )

          if (matchingDistrict) {
            districtSelect.value = matchingDistrict.value
            districtSelect.dispatchEvent(new Event('change', { bubbles: true }))
          }
        }
      }, 500)
    }
  }

  triggerFormUpdates() {
    const form = this.element.closest('form')
    if (form) {
      form.dispatchEvent(new CustomEvent('addressUpdated', { 
        bubbles: true, 
        detail: { fieldPrefix: this.fieldPrefixValue }
      }))
    }
  }

  // Handle address:added event from dynamic form controller
  handleAddressAdded(event) {
    const { addressForm } = event.detail
    if (addressForm) {
      this.addToNewElement(addressForm)
    }
  }

  // Public method to add autocomplete to new elements (for dynamic forms)
  addToNewElement(newElement) {
    const newInputs = newElement.querySelectorAll('input[data-address-autocomplete-target="input"]')
    newInputs.forEach(input => this.setupAutocomplete(input))
  }

  destroyAutocomplete() {
    this.autocompleteInstances.forEach(({ input, autocomplete }) => {
      if (autocomplete && google && google.maps) {
        google.maps.event.clearInstanceListeners(autocomplete)
      }
      input.classList.remove('places-autocomplete-enabled')
    })
    this.autocompleteInstances = []
  }
}